import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import api from "../lib/api";

const SEVERITY = {
  success: "border-l-[#114232]",
  info: "border-l-[#2c5b61]",
  warning: "border-l-[#d49a36]",
  danger: "border-l-[#c15337]",
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  async function load() {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {} // silent on auth bounces
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // 30s polling
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (!ref.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markAll() {
    await api.patch("/notifications/read-all");
    load();
  }
  async function markOne(id) {
    await api.patch(`/notifications/${id}/read`);
    load();
  }

  return (
    <div className="relative" ref={ref} data-testid="notifications-bell">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell-btn"
        className="relative p-2 rounded-md hover:bg-[#114232]/5 transition-colors"
      >
        <Bell className="w-4 h-4 text-[#5c6360]" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#c15337] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-[#114232]/15 shadow-[0_8px_30px_rgba(17,66,50,0.15)] z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#114232]/10">
            <div className="font-heading font-semibold text-sm text-[#114232]">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notifications-mark-all"
                className="text-[11px] font-semibold uppercase tracking-wider text-[#c15337] hover:underline inline-flex items-center gap-1"
              >
                <CheckCheck className="w-3 h-3" /> Mark all
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#5c6360]">No notifications yet.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => !n.read_at && markOne(n.id)}
                  data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-[#114232]/10 hover:bg-[#114232]/3 transition-colors border-l-2 ${SEVERITY[n.severity] || SEVERITY.info} ${
                    n.read_at ? "opacity-60" : ""
                  }`}
                >
                  <div className="text-xs font-semibold text-[#1b1d1c]">{n.title}</div>
                  <div className="text-xs text-[#5c6360] mt-0.5 leading-relaxed">{n.body}</div>
                  <div className="text-[10px] text-[#5c6360] mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
